import React, { useState } from 'react';

const SHORT_BIO = `Colleen Lavin is a Chicago-based comedian and writer. Her 2023 Edinburgh Fringe show Do the Robots Think I'm Funny featured an AI programmed to heckle her in real time. FringeReview called it "funny and unique." She's currently developing her new hour, This Was Supposed To Be Fun.`;

const LONG_BIO = `Colleen Lavin is a comedian and writer based in Chicago. She got laid off from her tech job on the first night of her honeymoon via a mass Zoom call, which is the kind of thing that builds character and also a new hour of stand-up.

Her 2023 Edinburgh Fringe show Do the Robots Think I'm Funny featured an AI she programmed to heckle her in real time. FringeReview called it "funny and unique" and noted that "the audience was riotous." The robot sometimes found her funny. She has chosen to take this at face value and not interrogate it further.

Her comedy is smart, neurotic, and obsessively well-structured—mostly about doing laundry during a dystopian hellscape and the quiet horror of realising you've built your entire personality around a job that can disappear in a calendar invite. She is, statistically, in the most trusted demographic for giving directions (brunette, glasses).

She's currently workshopping her next hour, This Was Supposed To Be Fun, a show about losing your job, raising your standards, and trying to have fun while everything falls apart.`;

const CURRENT_SHOW_DESC = `On the first night of her honeymoon, Colleen Lavin got laid off from her tech job on a mass Zoom call. Even worse, the swim-up bar was already closed for the night.

In This Was Supposed To Be Fun, Colleen explores the stagnant nature of post-AI culture, the absurd trivialities of wedding planning, and the decline of worker standards across industries. She also asks the important questions, like: when did everyone start playing pickleball? Why does her toothbrush now have a subscription tier? And under what circumstances should you complete a job application from a hot tub without telling your spouse?

Against the backdrop of a meticulously planned honeymoon, Colleen tries to work out what went wrong. Was it ambition? Burnout? The belief that if you just optimise your life enough, nothing bad can happen to you? Drawing on her experience inside the tech industry, she takes a close look at modern work culture, productivity, and the quiet panic of realising your entire sense of self might be a LinkedIn headline.

Smart, self-aware, and sharply written, this is a stand-up comedy show about losing your mind, raising your standards, and trying to have fun while everything is still very much falling apart.`;

interface LightboxProps {
  src: string;
  alt: string;
  onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ src, alt, onClose }) => (
  <div className="lightbox-overlay" onClick={onClose}>
    <button className="lightbox-close" onClick={onClose} aria-label="Close">
      &times;
    </button>
    <img src={src} alt={alt} className="lightbox-image" />
  </div>
);

const Press: React.FC = () => {
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);

  const openLightbox = (src: string, alt: string) => {
    setLightboxImage({ src, alt });
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  return (
    <section className="page press">
      <div className="container">
        <h2>Press Kit</h2>
        <p className="press-intro">
          Everything you need for listings, previews, and press.<br />
          If you need something that isn't here, please <a href="/contact">get in touch</a>.
        </p>

        <div className="press-kit-download">
          <a className="btn" href="/Colleen_Lavin_Press_Kit_2026.pdf" download>
            Download Press Kit (PDF)
          </a>
          <span className="btn-subtext">Printable version</span>
          <p className="press-kit-fallback">
            <a href="/Colleen_Lavin_Press_Kit_2026.pdf" target="_blank" rel="noreferrer">
              View PDF in new tab
            </a>
          </p>
        </div>

        {/* SHORT BIO */}
        <div className="press-kit-section">
          <h4>Short Bio</h4>
          <p className="bio-text">{SHORT_BIO}</p>
        </div>

        {/* LONG BIO */}
        <div className="press-kit-section">
          <h4>Long Bio</h4>
          {LONG_BIO.split('\n\n').map((para, i) => (
            <p key={i} className="bio-text">{para}</p>
          ))}
        </div>

        {/* CURRENT SHOW */}
        <div className="press-kit-section">
          <h4>Current Show</h4>
          <h3 className="show-title">This Was Supposed To Be Fun</h3>
          <span className="show-status">New Show — In Development</span>
          <div className="show-description">
            {CURRENT_SHOW_DESC.split('\n\n').map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>

        {/* PAST SHOW */}
        <div className="press-kit-section">
          <h4>Past Show</h4>
          <h3 className="show-title">Do the Robots Think I'm Funny?</h3>
          <p className="show-venue">Edinburgh Festival Fringe 2023</p>
          <p>
            A stand-up show featuring a live AI programmed to heckle the comedian in real time.
            FringeReview called it "funny and unique" and noted that "the audience was riotous."
          </p>
        </div>

        {/* SELECTED PRESS */}
        <div className="press-kit-section">
          <h4>Selected Press</h4>
          <div className="press-quotes">
            <div className="press-quote">
              <blockquote>Funny and unique</blockquote>
              <cite>FringeReview</cite>
            </div>
            <div className="press-quote">
              <blockquote>The audience was riotous</blockquote>
              <cite>FringeReview</cite>
            </div>
          </div>
          <div className="press-links">
            <ul>
              <li>
                <a
                  href="https://fringereview.co.uk/review/edinburgh-fringe/2023/colleen-lavin-do-the-robots-think-im-funny/"
                  target="_blank"
                  rel="noreferrer"
                >
                  FringeReview — Do the Robots Think I'm Funny?
                </a>
              </li>
              <li>
                <a
                  href="https://thepodcastcorner.com/2023/08/17/the-edinburgh-fringe-show-2023-colleen-lavin-do-the-robots-think-im-funny/"
                  target="_blank"
                  rel="noreferrer"
                >
                  The Edinburgh Fringe Show Podcast Interview
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* PHOTOS */}
        <div className="press-kit-section">
          <h4>Photos</h4>
          <p className="photo-label">Press-approved images</p>
          <div className="press-photos">
            <button
              className="press-photo-thumb"
              onClick={() => openLightbox('/cool.jpg', 'Colleen Lavin — Studio portrait')}
              aria-label="View studio portrait"
            >
              <img src="/cool.jpg" alt="Colleen Lavin — Studio portrait" />
              <span className="photo-caption">Studio portrait</span>
            </button>
            <button
              className="press-photo-thumb"
              onClick={() => openLightbox('/primary2.jpg', 'Colleen Lavin — Live mic shot')}
              aria-label="View live mic shot"
            >
              <img src="/primary2.jpg" alt="Colleen Lavin — Live mic shot" />
              <span className="photo-caption">Live mic shot</span>
            </button>
            <button
              className="press-photo-thumb"
              onClick={() => openLightbox('/hero.png', 'Colleen Lavin — Promo')}
              aria-label="View promo"
            >
              <img src="/hero.png" alt="Colleen Lavin — Promo" />
              <span className="photo-caption">Promo</span>
            </button>
          </div>
        </div>

        {/* CONTACT */}
        <div className="press-kit-section">
          <h4>Contact</h4>
          <p>
            <strong>Email:</strong> <a href="mailto:colleen@colleenlavin.com">colleen@colleenlavin.com</a>
          </p>
          <p>
            <strong>Social:</strong> <a href="https://instagram.com/colleenjokes" target="_blank" rel="noreferrer">@colleenjokes</a>
          </p>
        </div>

        {/* BOTTOM DOWNLOAD BUTTON */}
        <div className="press-kit-download press-kit-download-bottom">
          <a className="btn" href="/Colleen_Lavin_Press_Kit_2026.pdf" download>
            Download Press Kit (PDF)
          </a>
          <span className="btn-subtext">Printable version</span>
        </div>

        <p className="press-kit-updated">Last updated: 2026</p>
      </div>

      {lightboxImage && (
        <Lightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          onClose={closeLightbox}
        />
      )}
    </section>
  );
};

export default Press;
